/* Fog & Form — interface instruments (design-system layer v4).
   Small pieces of feedback, each independent and each a no-op when its markup is absent:
     1. Header: one marker that glides between menu items.
     2. Pointer light: feeds --mx / --my to product tiles and the survey-grid sections.
     3. Product page loupe: a 2x round lens over the main photograph (click still opens zoom).
     4. Decode: mono labels settle into place once, the first time they are seen.
     5. Count: catalog entry counts and spec-strip figures run up once when first seen.
     6. Search key: "/" opens search (the theme already answers Cmd+K on a Mac); the
        header shows the key beside the search icon.
   Pointer effects need a mouse or trackpad; motion is skipped under prefers-reduced-motion
   and inside the theme editor. No dependencies. Styles are in fog-design-system-v4.css. */
(function () {
  'use strict';

  var doc = document;
  var html = doc.documentElement;
  var mm = function (q) { return !!(window.matchMedia && window.matchMedia(q).matches); };
  var reduce = mm('(prefers-reduced-motion: reduce)');
  var fine = mm('(hover: hover) and (pointer: fine)');
  var design = html.classList.contains('shopify-design-mode');
  var motion = !reduce && !design;

  /* ---------------------------------------------------------------------- 1 */
  function bindNav() {
    if (!fine) return;
    // The theme re-renders the header menu after load, which would drop a marker placed
    // up front, so it is created (or re-attached) on the first hover and whenever missing.
    var ind = null;
    var nav = null;
    var on = false;

    function ensure(n) {
      if (!ind) {
        ind = doc.createElement('span');
        ind.className = 'ff-nav-ind';
        ind.setAttribute('aria-hidden', 'true');
      }
      if (ind.parentNode !== n) { n.appendChild(ind); on = false; }
      nav = n;
    }
    function move(link) {
      var target = link.querySelector('.menu-list__link-title') || link;
      var a = target.getBoundingClientRect();
      var b = nav.getBoundingClientRect();
      if (!a.width) return;
      // First appearance: place it without travelling in from the left edge.
      if (!on) ind.classList.add('ff-nav-ind--snap');
      ind.style.setProperty('--x', (a.left - b.left) + 'px');
      ind.style.setProperty('--w', a.width + 'px');
      if (!on) {
        void ind.offsetWidth;
        ind.classList.remove('ff-nav-ind--snap');
        on = true;
      }
      ind.classList.add('is-on');
    }
    function hide() { on = false; if (ind) ind.classList.remove('is-on'); }
    function point(e) {
      var t = e.target;
      var item = t && t.closest ? t.closest('.menu-list__list-item') : null;
      var n = item ? item.closest('header-menu nav.menu-list') : null;
      if (!n) { if (on && !(nav && t && nav.contains(t))) hide(); return; }
      if (item.getAttribute('slot') === 'overflow') return;
      var link = item.querySelector('.menu-list__link');
      if (!link) return;
      ensure(n);
      move(link);
    }
    doc.addEventListener('pointerover', point, { passive: true });
    doc.addEventListener('focusin', point);
  }

  /* ---------------------------------------------------------------------- 2 */
  function bindPointerLight() {
    if (!fine || !motion) return;
    var raf = 0;
    var last = null;
    function paint() {
      raf = 0;
      if (!last) return;
      var r = last.el.getBoundingClientRect();
      last.el.style.setProperty('--mx', Math.round(last.x - r.left) + 'px');
      last.el.style.setProperty('--my', Math.round(last.y - r.top) + 'px');
    }
    doc.addEventListener('pointermove', function (e) {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      var t = e.target;
      if (!t || !t.closest) return;
      var el = t.closest('.product-card .card-gallery, .ff-man, .ff-idx');
      if (!el) return;
      last = { el: el, x: e.clientX, y: e.clientY };
      if (!raf) raf = window.requestAnimationFrame(paint);
    }, { passive: true });
  }

  /* ---------------------------------------------------------------------- 3 */
  function bindLens() {
    if (!fine || design) return;
    var MAX_ZOOM = 2;       // never more than twice the size on the page
    var MIN_ZOOM = 1.5;     // below this the glass is not worth showing
    var STRETCH = 1.35;     // how far past its real pixels a photograph may be enlarged
    var zoom = MAX_ZOOM;
    var SEL = '.product-information media-gallery .product-media-container--image';
    var lens = null;
    var slide = null;
    var img = null;
    var raf = 0;
    var pt = null;

    function drawn(image, box) {
      // Where the picture actually sits inside its box (object-fit: contain or cover).
      var nw = image.naturalWidth, nh = image.naturalHeight;
      if (!nw || !nh) return { x: 0, y: 0, w: box.width, h: box.height };
      var fit = window.getComputedStyle(image).objectFit;
      var s = fit === 'cover' ? Math.max(box.width / nw, box.height / nh)
        : (fit === 'contain' || fit === 'scale-down') ? Math.min(box.width / nw, box.height / nh)
        : null;
      if (s === null) return { x: 0, y: 0, w: box.width, h: box.height };
      var w = nw * s, h = nh * s;
      return { x: (box.width - w) / 2, y: (box.height - h) / 2, w: w, h: h };
    }
    function off() {
      if (lens) lens.classList.remove('is-on');
      slide = null; img = null; pt = null;
    }
    function paint() {
      raf = 0;
      if (!slide || !img || !pt) return;
      var sb = slide.getBoundingClientRect();
      var ib = img.getBoundingClientRect();
      var d = drawn(img, ib);
      // Magnify only as far as the file's real pixels allow, so the glass stays sharp.
      zoom = Math.min(MAX_ZOOM, (img.__ffReal / d.w) * STRETCH);
      if (zoom < MIN_ZOOM) { lens.classList.remove('is-on'); return; }
      var size = lens.offsetWidth || 208;
      var cx = pt.x - sb.left, cy = pt.y - sb.top;
      var px = pt.x - ib.left - d.x, py = pt.y - ib.top - d.y;
      lens.style.backgroundSize = (d.w * zoom) + 'px ' + (d.h * zoom) + 'px';
      lens.style.backgroundPosition = (size / 2 - px * zoom) + 'px ' + (size / 2 - py * zoom) + 'px';
      lens.style.transform = 'translate3d(' + cx + 'px,' + cy + 'px,0)';
      lens.classList.add('is-on');
    }
    doc.addEventListener('pointermove', function (e) {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      if (window.innerWidth < 990) { if (slide) off(); return; }
      var t = e.target;
      var s = t && t.closest ? t.closest(SEL) : null;
      if (!s || e.buttons) { if (slide) off(); return; }
      if (s !== slide || !lens || !lens.isConnected) {
        var image = s.querySelector('img.product-media__image, .product-media img');
        if (!image || !image.complete || !image.naturalWidth) { if (slide) off(); return; }
        if (!image.__ffReal) {
          // naturalWidth is density-corrected when srcset is used; measure the file itself.
          if (!image.__ffProbe) {
            image.__ffProbe = new Image();
            image.__ffProbe.onload = function () { image.__ffReal = image.__ffProbe.naturalWidth; };
            image.__ffProbe.src = image.currentSrc || image.src;
          }
          if (slide) off();
          return;
        }
        if (!lens) {
          lens = doc.createElement('span');
          lens.className = 'ff-lens';
          lens.setAttribute('aria-hidden', 'true');
        }
        if (window.getComputedStyle(s).position === 'static') s.style.position = 'relative';
        s.appendChild(lens);
        lens.style.backgroundImage = 'url("' + (image.currentSrc || image.src) + '")';
        slide = s; img = image;
      }
      pt = { x: e.clientX, y: e.clientY };
      if (!raf) raf = window.requestAnimationFrame(paint);
    }, { passive: true });
    doc.addEventListener('pointerdown', function () { if (slide) off(); }, { passive: true });
    doc.addEventListener('scroll', function () { if (slide) off(); }, { passive: true });
    doc.documentElement.addEventListener('pointerleave', function () { if (slide) off(); });
  }

  /* ---------------------------------------------------------------------- 4 */
  var UP = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  var LOW = 'abcdefghijklmnopqrstuvwxyz';
  var NUM = '0123456789';
  function pick(set) { return set.charAt(Math.floor(Math.random() * set.length)); }
  function decode(el, dur) {
    if (!motion || !el || el.__ffDecoded) return;
    // Plain text only: anything with links or markup inside is left alone.
    if (el.childNodes.length !== 1 || el.firstChild.nodeType !== 3) return;
    var text = el.textContent;
    if (!text || !text.replace(/\s/g, '') || text.length > 48) return;
    el.__ffDecoded = true;

    var sr = doc.createElement('span');
    sr.className = 'visually-hidden';
    sr.textContent = text;
    var out = doc.createElement('span');
    out.className = 'ff-decoding';
    out.setAttribute('aria-hidden', 'true');
    el.textContent = '';
    el.appendChild(sr);
    el.appendChild(out);

    var chars = text.split('');
    var t0 = 0;
    var lastFrame = 0;
    function frame(now) {
      if (!t0) t0 = now;
      if (!out.isConnected) return;          // something else rewrote the label; leave it be
      var p = Math.min(1, (now - t0) / dur);
      if (p >= 1) { el.textContent = text; return; }
      if (now - lastFrame > 34) {
        lastFrame = now;
        var settled = Math.floor(p * chars.length);
        var s = '';
        for (var i = 0; i < chars.length; i++) {
          var c = chars[i];
          if (i < settled) s += c;
          else if (UP.indexOf(c) > -1) s += pick(UP);
          else if (LOW.indexOf(c) > -1) s += pick(LOW);
          else if (NUM.indexOf(c) > -1) s += pick(NUM);
          else s += c;
        }
        out.textContent = s;
      }
      window.requestAnimationFrame(frame);
    }
    window.requestAnimationFrame(frame);
  }

  function bindDecode() {
    if (!motion) return;

    // 4a. Live readouts in the conditions rail decode as each one arrives.
    var cells = doc.querySelectorAll('[data-ff-conditions] [data-ff-live]');
    function arrive(cell) {
      var dd = cell.querySelector('dd');
      if (dd && !cell.hidden) decode(dd, 620);
    }
    for (var i = 0; i < cells.length; i++) {
      arrive(cells[i]);
      if (window.MutationObserver) {
        new MutationObserver(function (list) {
          for (var m = 0; m < list.length; m++) arrive(list[m].target);
        }).observe(cells[i], { attributes: true, attributeFilter: ['hidden'] });
      }
    }

    // 4b. Section kickers decode the first time they scroll into view.
    if (!window.IntersectionObserver) return;
    var labels = doc.querySelectorAll('.section-resource-list__header .text-block :is(h5, h6), .ff-man .ff-kicker, .ff-idx .ff-kicker');
    if (!labels.length) return;
    var io = new IntersectionObserver(function (entries) {
      for (var e = 0; e < entries.length; e++) {
        if (!entries[e].isIntersecting) continue;
        io.unobserve(entries[e].target);
        decode(entries[e].target, 520);
      }
    }, { rootMargin: '0px 0px -8% 0px' });
    for (var l = 0; l < labels.length; l++) io.observe(labels[l]);
  }

  /* ---------------------------------------------------------------------- 5 */
  function bindCount() {
    if (!motion || !window.IntersectionObserver) return;
    var counts = doc.querySelectorAll('.ff-idx .ff-c, [data-ff-tick]');
    if (!counts.length) return;
    function run(el, delay) {
      if (el.__ffCounted) return;
      el.__ffCounted = true;
      var m = /^(\D*)(\d+)([\s\S]*)$/.exec(el.textContent);
      if (!m) {
        // Not a number: a short word settles into place instead.
        if (el.hasAttribute('data-ff-decode')) window.setTimeout(function () { decode(el, 640); }, delay);
        return;
      }
      var end = parseInt(m[2], 10);
      if (!end || end > 9999) return;
      var final = el.textContent;
      var dur = 700 + Math.min(500, end * 12);
      var t0 = 0;
      function frame(now) {
        if (!t0) t0 = now + delay;
        var p = Math.max(0, Math.min(1, (now - t0) / dur));
        if (p >= 1) { el.textContent = final; return; }
        var eased = 1 - Math.pow(1 - p, 3);
        el.textContent = m[1] + Math.round(end * eased) + m[3];
        window.requestAnimationFrame(frame);
      }
      window.requestAnimationFrame(frame);
    }
    var io = new IntersectionObserver(function (entries) {
      var n = 0;
      for (var e = 0; e < entries.length; e++) {
        if (!entries[e].isIntersecting) continue;
        io.unobserve(entries[e].target);
        run(entries[e].target, n * 45);
        n++;
      }
    }, { rootMargin: '0px 0px -6% 0px' });
    for (var c = 0; c < counts.length; c++) io.observe(counts[c]);
  }

  /* ---------------------------------------------------------------------- 6 */
  function bindSearchKey() {
    var mac = /mac|iphone|ipad/i.test((navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || '');
    html.setAttribute('data-ff-key', mac ? 'mac' : 'other');
    doc.addEventListener('keydown', function (e) {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey || e.defaultPrevented) return;
      var t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName || ''))) return;
      if (doc.querySelector('dialog[open]')) return;
      var buttons = doc.querySelectorAll('search-button button');
      for (var i = 0; i < buttons.length; i++) {
        if (buttons[i].offsetParent !== null) {
          e.preventDefault();
          buttons[i].click();
          return;
        }
      }
    });
  }

  function init() {
    bindNav();
    bindSearchKey();
    bindPointerLight();
    bindLens();
    bindDecode();
    bindCount();
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init);
  else init();
})();
